import { useEffect, useState } from 'react';

import { Theme, useMediaQuery } from '@mui/material';

import { CoreBox, CoreFormControlLabel, CoreRadio, CoreTextField, CoreDialog, CoreTypography } from '@youscience/core';

import useSchoolSurveyStore from '@store/SchoolSurveyStore';

import { FAVORITE_SCHOOLS_QUESTION_ID, SURVEYS_ID, SCHOOL_QUESTIONS_DATA } from '@constants/surveys';

import { SchoolSurveyData, SurveyQuestionsData } from '@interfaces/favoriteSurvey';
import { InitialModalsStatus } from '@interfaces/optIn';

import DialogFooter from '@components/DialogFooter';

import {
  sxStyles,
  StyledDialogTitle,
  StyledDialogContent,
  StyledScoreRadioButton,
  StyledRadioGroup,
  StyledScoreButtonsWrap,
  StyledRadio,
} from './SchoolSurvey.styles';

interface SchoolSurveyProps {
  schoolInfo: SchoolSurveyData;
  isOpenSurvey: boolean;
  onCloseSurvey: () => void;
  isOpenFromNotes: boolean;
  setActiveModal: (field?: keyof InitialModalsStatus) => void;
}

const CALCULATE_COST_MESSAGE = 'You can calculate the cost of attending by visiting the school’s website.';

export const SchoolSurvey = (props: SchoolSurveyProps) => {
  const {
    schoolInfo: { id, name, calculateLink = '' },
    isOpenSurvey,
    isOpenFromNotes,
    onCloseSurvey,
    setActiveModal,
  } = props;

  const formattedLink = calculateLink.match(/^[a-zA-Z]+:\/\//) ? calculateLink : `https://${calculateLink}`;

  const isLoading = useSchoolSurveyStore((state) => state.isLoading);

  const setSchoolFavoriteSurvey = useSchoolSurveyStore((state) => state.setSchoolFavoriteSurvey);
  const getSchoolFavoriteSurvey = useSchoolSurveyStore((state) => state.getSchoolFavoriteSurvey);

  const isDesktopScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

  const initialSurveyData = {} as SurveyQuestionsData;
  const [temporarySurveyData, setTemporarySurveyData] = useState(initialSurveyData);

  const handleRadioChange = (questionId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setTemporarySurveyData((prevState) => ({
      ...prevState,
      [questionId]: event.target.value,
    }));
  };

  const handleOpenQuestionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.trim().length > 2000) return;

    setTemporarySurveyData((prevState) => ({
      ...prevState,
      [FAVORITE_SCHOOLS_QUESTION_ID.FOURTH]: event.target.value,
    }));
  };

  const closeSurvey = () => {
    onCloseSurvey();

    if (isOpenFromNotes) {
      setActiveModal('successDialog');
    } else {
      setActiveModal('optInConnection');
    }
  };

  const handleSkipSurveyData = () => {
    closeSurvey();
    setTemporarySurveyData(initialSurveyData);
  };

  const handleSaveSurveyData = () => {
    if (JSON.stringify(initialSurveyData) !== JSON.stringify(temporarySurveyData)) {
      const preparedQuestionsData = Object.entries(temporarySurveyData).map(([key, value]) => ({
        questionId: +key,
        answer: value,
      }));

      const postData = {
        collegeId: +id,
        surveyId: SURVEYS_ID.FAVORITE_SCHOOLS,
        questions: preparedQuestionsData,
      };

      setSchoolFavoriteSurvey(postData);
    }

    closeSurvey();
  };

  useEffect(() => {
    const getSurvey = async () => {
      const surveyQuestionData = await getSchoolFavoriteSurvey(id);

      setTemporarySurveyData(surveyQuestionData);
    };

    if (!Object.keys(initialSurveyData).length) {
      void getSurvey();
    }
  }, []);

  return (
    <CoreDialog
      sx={sxStyles.dialogWrap}
      open={isOpenSurvey}
      onClose={handleSkipSurveyData}
      aria-labelledby="survey-title"
    >
      <StyledDialogTitle id="survey-title">{`What do you like about ${name}?`}</StyledDialogTitle>

      <StyledDialogContent data-testid="surveyContent">
        <CoreTypography sx={sxStyles.subtitle}>
          Help us understand why you like this school by answering the questions below.
        </CoreTypography>

        {SCHOOL_QUESTIONS_DATA.map((question) => {
          const isNotSureChecked = temporarySurveyData[question.id] === '0';

          return (
            <CoreBox sx={sxStyles.questionWrap} key={question.id}>
              <CoreTypography sx={sxStyles.question} variant="h5" component="h4">
                {question.title}
              </CoreTypography>

              <StyledRadioGroup onChange={handleRadioChange(question.id)}>
                <StyledScoreButtonsWrap>
                  {question.scoreData.map(({ value, label }) => {
                    const isSelected = value === temporarySurveyData[question.id];

                    return (
                      <CoreBox key={value}>
                        <StyledScoreRadioButton isSelected={isSelected} disabled={isLoading}>
                          <StyledRadio value={value} />

                          <CoreTypography>{value}</CoreTypography>
                        </StyledScoreRadioButton>

                        {(isDesktopScreen || +value % 2 !== 0) && (
                          <CoreTypography sx={sxStyles.scoreLabel}>{label}</CoreTypography>
                        )}
                      </CoreBox>
                    );
                  })}
                </StyledScoreButtonsWrap>

                <CoreFormControlLabel
                  control={<CoreRadio checked={isNotSureChecked} value="0" disabled={isLoading} />}
                  label="Not sure"
                />

                {isNotSureChecked && (
                  <CoreBox>
                    {question.id === FAVORITE_SCHOOLS_QUESTION_ID.SECOND ? (
                      <CoreTypography sx={sxStyles.notSureMessage}>
                        {calculateLink.length > 1 ? (
                          <>
                            You can calculate the costs of attending in the &nbsp;
                            <a href={formattedLink} rel="noreferrer" target="_blank">
                              net price calculator.
                            </a>
                          </>
                        ) : (
                          CALCULATE_COST_MESSAGE
                        )}
                      </CoreTypography>
                    ) : (
                      <CoreTypography>{question.notSureMessage}</CoreTypography>
                    )}
                  </CoreBox>
                )}
              </StyledRadioGroup>
            </CoreBox>
          );
        })}

        <CoreBox>
          <CoreTypography sx={sxStyles.notesTitle} variant="h5" component="h4">
            What else would you like to know as you look into this school?
          </CoreTypography>

          {!isLoading && (
            <CoreTextField
              sx={sxStyles.notesField}
              placeholder="Please write your notes here"
              multiline
              fullWidth
              rows={2}
              value={temporarySurveyData[FAVORITE_SCHOOLS_QUESTION_ID.FOURTH]}
              onChange={handleOpenQuestionChange}
              disabled={isLoading}
            />
          )}
        </CoreBox>
      </StyledDialogContent>

      <DialogFooter
        goBackButtonTitle="Skip the survey"
        goNextButtonTitle="Save"
        goNextActionButtonTitle="Saving"
        onGoBack={handleSkipSurveyData}
        onGoNext={handleSaveSurveyData}
      />
    </CoreDialog>
  );
};
